import { getFeatureConfig } from '@luna/feature-config';
import { getToken, removeAuthToken } from '@shared/auth';
import { flushTrackingQueue, stopTracking } from '@shared/statistics';
import { logger } from '@luna/logger';
import { getDarkModeState } from '@shared/dark-mode';
import { createQueryString } from '@luna/router';
import { getCurrentLanguage } from '@luna/i18n';

import { getHandoverState } from './getHandoverState';
import { triggerTokenExchange } from './triggerTokenExchange';
import { addAccessTokenToUrl, appendQueryString, getSessionIdOrUndefined } from './UrlUtils';

export interface HandoffParams {
    url: string; // the handoff URL
    targetClientId?: string; // required for token exchange
    target?: string; // required by some APIs to do the handoff
    returnRoute?: string;
    oidcReady?: boolean;
    preferTokenExchange?: boolean;
    passParamsInQuery?: boolean;
    passCurrentAccessToken?: boolean;
}

const stripHashFromRoute = (route: string): string => route.replace('#/', '').replace('#', '');

const isParamDefined = (param: string): boolean => {
    return !param.endsWith('=');
};

/**
 * Use this function to `navigate()` to the handoff URLs in your React components.
 *
 * If the application is configured to use OIDC or SSO, the user will be redirected
 * to the external application.
 *
 * Otherwise, it will trigger a token exchange and redirect to the external application,
 * while making sure to logout from George.
 *
 * An error is shown on the /handoff/exchange/ landing page if the URL is incorrect or
 * if no hash exists.
 *
 * A spinner is shown on the /handoff/exchange/ landing page while the
 * token exchange is taking place.
 *
 * @param options Requires `url` and `targetClientId`; Optional `returnRoute`, `oidcReady`, `target`
 */
export const getHandoffUrl = ({
    url,
    returnRoute = window.location.hash,
    targetClientId = '',
    oidcReady = false,
    target = '',
    preferTokenExchange = false,
    passParamsInQuery = false,
    passCurrentAccessToken = false,
}: HandoffParams): string => {
    const params = [
        `returnRoute=${encodeURIComponent(stripHashFromRoute(returnRoute))}`,
        `targetClientId=${encodeURIComponent(targetClientId)}`,
        `oidcReady=${encodeURIComponent(oidcReady)}`,
        `target=${target}`,
        `preferTokenExchange=${preferTokenExchange}`,
        `passParamsInQuery=${passParamsInQuery}`,
    ];

    const path = [
        `#/handoff/exchange/?url=${encodeURIComponent(url)}`,
        ...params.filter(isParamDefined),
    ].join('&');

    if (passCurrentAccessToken) {
        return `${path}&passCurrentAccessToken=true`;
    }

    return path;
};

export const getHandoffParamsFromHash = (hash: string): HandoffParams => {
    const hashSearchParams = hash.split('?')[1];
    const searchParams = new URLSearchParams(hashSearchParams);
    const url = decodeURIComponent(searchParams.get('url') ?? '');
    const targetClientId = decodeURIComponent(searchParams.get('targetClientId') ?? '');
    const target = decodeURIComponent(searchParams.get('target') ?? '');
    const returnRoute = decodeURIComponent(searchParams.get('returnRoute') ?? '');
    const oidcReady = decodeURIComponent(searchParams.get('oidcReady') ?? '');
    const preferTokenExchange = decodeURIComponent(searchParams.get('preferTokenExchange') ?? '');
    const passParamsInQuery = decodeURIComponent(searchParams.get('passParamsInQuery') ?? '');
    const passCurrentAccessToken = decodeURIComponent(
        searchParams.get('passCurrentAccessToken') ?? '',
    );

    return {
        url,
        targetClientId,
        target: target === '' ? undefined : target,
        returnRoute: stripHashFromRoute(returnRoute),
        oidcReady: oidcReady.toLowerCase() === 'true',
        preferTokenExchange: preferTokenExchange.toLowerCase() === 'true',
        passParamsInQuery: passParamsInQuery.toLowerCase() === 'true',
        passCurrentAccessToken: passCurrentAccessToken.toLowerCase() === 'true',
    };
};

const shouldRedirectWithOIDC = (oidcReady: boolean): boolean =>
    oidcReady || getFeatureConfig((config) => config.handover.sso);

const shouldRedirectWithExchangedToken = (): boolean =>
    getFeatureConfig((config) => config.handover.handoverTokenExchange);

export const getHandoffTargetUrl = async ({
    url,
    targetClientId,
    target,
    returnRoute = '',
    passParamsInQuery = false,
    oidcReady = false,
    preferTokenExchange = false,
    passCurrentAccessToken = false,
}: HandoffParams): Promise<string> => {
    const targetUrl = new URL(url);
    const queryString = createQueryString(
        {
            lang: getCurrentLanguage(),
            darkMode: getDarkModeState().isActive,
            state: getHandoverState(stripHashFromRoute(returnRoute), target),
            sessionId: getSessionIdOrUndefined(),
        },
        { withLeadingQuestionMark: false },
    );

    if (passParamsInQuery) {
        targetUrl.search = appendQueryString(targetUrl.search, queryString);
    } else {
        targetUrl.hash = appendQueryString(targetUrl.hash, queryString);
    }

    if (passCurrentAccessToken) {
        addAccessTokenToUrl(targetUrl, getToken());
        return targetUrl.toString();
    }

    if (!preferTokenExchange && shouldRedirectWithOIDC(oidcReady)) {
        return targetUrl.toString();
    }

    if (shouldRedirectWithExchangedToken()) {
        const accessToken = await triggerTokenExchange(targetClientId);
        addAccessTokenToUrl(targetUrl, accessToken);

        return targetUrl.toString();
    }

    throw new Error(`No valid handover configuration for URL: ${url}`);
};

const FLUSH_QUEUE_TIMEOUT = 1000;

const handleRedirect = async (targetUrl: string): Promise<void> => {
    stopTracking();
    try {
        await Promise.race([
            flushTrackingQueue(),
            new Promise((res) => {
                setTimeout(() => res(null), FLUSH_QUEUE_TIMEOUT);
            }),
        ]);
    } catch {
        logger.warn('Failed to flush tracking queues before handoff redirect');
    }

    removeAuthToken();

    window.location.replace(targetUrl);
};

export const doHandoffWithoutInfoPage = async (params: HandoffParams): Promise<void> => {
    const handoffTargetUrl = await getHandoffTargetUrl(params);
    await handleRedirect(handoffTargetUrl);
};
